import { Button } from "@/components/ui/button";
import HeroCarousel from "@/components/HeroCarousel";
import FeatureHighlights from "@/components/FeatureHighlights";
import recoverMeLogo from "@assets/forgerestore-logo_1779932627674.png";
import { LogIn, ArrowRight, FlaskConical, HelpCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { MeshGradient } from "@/components/visual/MeshGradient";
import { useAppearance } from "@/lib/appearance";

const HEADLINES = [
  "Rebuild. Reinforce. Reclaim.",
  "One step. One day. One self.",
  "Real recovery. Real privacy.",
];

function HeadlineRotator() {
  const { isReducedMotion } = useAppearance();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (isReducedMotion) return;
    const t = setInterval(
      () => setIndex((p) => (p + 1) % HEADLINES.length),
      3500,
    );
    return () => clearInterval(t);
  }, [isReducedMotion]);

  return (
    <div
      className="text-center mb-6 min-h-[3.5rem] sm:min-h-[4.5rem] flex items-center justify-center"
      aria-live="polite"
      data-testid="headline-rotator"
    >
      <AnimatePresence mode="wait">
        <motion.h2
          key={index}
          initial={isReducedMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={isReducedMotion ? { opacity: 1 } : { opacity: 0, y: -12 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-primary via-foreground to-primary bg-clip-text text-transparent"
        >
          {HEADLINES[index]}
        </motion.h2>
      </AnimatePresence>
    </div>
  );
}

export default function Landing() {
  const { isReducedMotion } = useAppearance();
  // Task #362 — overflow-x-clip (not overflow-hidden): `hidden` made the
  // wrapper a scroll container, silently disabling the header's
  // position:sticky, and on phones the page could still be laid out wider
  // than the screen, cutting the Log In button off past the right edge.
  // `clip` prevents any horizontal overflow/panning without creating a
  // scroll container, so the header genuinely sticks. MeshGradient clips
  // its own blobs, so no vertical clipping is needed here.
  return (
    <div className="relative min-h-screen bg-background overflow-x-clip">
      <MeshGradient className="z-0" />

      <div className="relative z-10">
        <header className="sticky top-0 z-50 bg-background/70 backdrop-blur-md border-b border-border/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-2.5 flex items-center">
            <motion.div
              initial={isReducedMotion ? false : { x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-2.5 min-w-0"
            >
              {/* Task #189 — brand lockup is locked to fixed ForgeRestore
                  palette regardless of the user's selected app theme so the
                  pre-auth identity stays consistent. */}
              <img src={recoverMeLogo} alt="ForgeRestore" width={36} height={36} className="w-9 h-9 object-contain" />
              <div className="flex flex-col leading-none">
                <span className="font-bold text-lg tracking-tight text-[#0A4D68] dark:text-[#FFB84C]">ForgeRestore</span>
                <span className="text-[10px] tracking-wide uppercase mt-0.5 text-[#0A4D68]/70 dark:text-[#FFB84C]/80" data-testid="text-brand-tagline">
                  Rebuild · Reinforce · Reclaim
                </span>
              </div>
            </motion.div>
          </div>
        </header>

        <main className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4 sm:py-8 md:py-12">
          {/* Task #334 — visually-hidden H1: React replaces the server-injected
              #root H1 on mount, so the rendered DOM needs its own top-level
              heading for Googlebot and screen readers. Text mirrors
              server/publicMetadata.ts. */}
          <h1 className="sr-only">
            ForgeRestore: A Private 12-Step Recovery App for Sponsors and Sponsees
          </h1>
          <motion.section
            initial={isReducedMotion ? false : { y: -8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="mb-3 sm:mb-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm"
            aria-label="Account access"
            data-testid="landing-access-actions"
          >
            <span className="text-muted-foreground">Already have an account?</span>
            <Button
              onClick={() => window.location.href = "/api/login"}
              data-testid="button-login-landing"
              className="h-10 px-5 text-sm font-semibold shadow-md hover:shadow-lg transition-shadow"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Log In
            </Button>
          </motion.section>
          <motion.div
            initial={isReducedMotion ? false : { y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-3 sm:mb-4"
          >
            <HeroCarousel />
          </motion.div>

          <HeadlineRotator />

          <motion.div
            initial={isReducedMotion ? false : { y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center mb-10 sm:mb-14"
          >
            <div
              className="inline-flex flex-col items-center justify-center gap-2.5 rounded-xl border border-border/50 bg-card/70 p-3 sm:flex-row sm:gap-3 sm:p-4 backdrop-blur-md shadow-md"
              data-testid="landing-account-options"
            >
              <span className="text-sm text-muted-foreground sm:mr-1">
                New here? Choose your account path.
              </span>
              <Button
                onClick={() => window.location.href = "/api/login"}
                data-testid="button-create-account"
                className="font-semibold px-5 shadow-sm hover:shadow-md transition-all group"
              >
                Create Account
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = "/api/login?beta=true"}
                data-testid="button-create-beta-account"
                className="font-semibold px-5 shadow-sm hover:shadow-md transition-all group"
              >
                <FlaskConical className="w-5 h-5 mr-2" />
                Create Beta-Tester Account
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={isReducedMotion ? false : { y: 30, opacity: 0 }}
            whileInView={isReducedMotion ? undefined : { y: 0, opacity: 1 }}
            animate={isReducedMotion ? { y: 0, opacity: 1 } : undefined}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <FeatureHighlights />
          </motion.div>
        </main>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pb-6">
          <motion.div
            initial={isReducedMotion ? false : { y: 20, opacity: 0 }}
            whileInView={isReducedMotion ? undefined : { y: 0, opacity: 1 }}
            animate={isReducedMotion ? { y: 0, opacity: 1 } : undefined}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex items-start gap-3 p-4 rounded-md bg-muted/50 border border-border/50"
            data-testid="login-help-note"
          >
            <HelpCircle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">Trouble signing in?</p>
              <p>
                ForgeRestore uses your Replit account to sign in. If you can't access your account,
                reset your password through your sign-in provider (Google, Apple, or email) at
                their respective account recovery pages. Your ForgeRestore data is safely stored and
                will be here when you get back in.
              </p>
            </div>
          </motion.div>
        </div>

        <footer className="border-t border-border/50 py-6 mt-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
            <span>© 2026 ForgeRestore. Your data is never sold.</span>
            <a
              href="/privacy-policy"
              className="underline hover:text-foreground transition-colors"
              data-testid="link-privacy-policy-footer"
            >
              Privacy Policy
            </a>
            <a
              href="/terms"
              className="underline hover:text-foreground transition-colors"
              data-testid="link-terms-footer"
            >
              Terms &amp; Conditions
            </a>
            <a
              href="/resources"
              className="underline hover:text-foreground transition-colors"
              data-testid="link-resources-footer"
            >
              Resources
            </a>
            <span className="text-xs">
              Peer-recovery tool for general wellness use. Not a medical device.
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}
